import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Input,
  OnChanges,
  ViewContainerRef, OnDestroy,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {interval, Subscription} from 'rxjs';
import {filter, first} from 'rxjs/operators';

@Component({
  selector: 'app-tooltip',
  templateUrl: './tooltip.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tooltip.component.scss']
})
export class TooltipComponent {

  readonly tooltipContainer = viewChild<ElementRef>('tooltipContainer');

  tooltip: string|undefined;
  left = 0;
  top = 0;
  visible = false;
  /** Enables the slide/fade transition only once the final position has been committed. */
  animate = false;
  position: 'left'|'right'|'top'|'bottom' = TooltipPosition.BOTTOM;
  delta = 0;
  maxWidth: 200|2000 = 200;

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private destroyed = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.destroyed = true);
  }

  /**
   * Re-renders this tooltip now. The directive mutates the properties above from timers and
   * requestAnimationFrame callbacks; when the tooltip is declared inside an OnPush component
   * nothing marks that subtree dirty, so the global tick would skip it and the tooltip would
   * only show up on the next click. Driving this view's CD directly works for both a declared
   * `<app-tooltip>` and a dynamically-created one, regardless of the host's CD strategy.
   */
  refresh(): void {
    if (!this.destroyed) {
      this.changeDetectorRef.detectChanges();
    }
  }
}

@Directive({
  selector: '[tooltip],[tooltipTriggerFor]',
  // A hover hint, for a mouse or a pen. A finger has no hover: the tap's emulated mouseenter showed the tooltip
  // and no mouseleave followed, so it stayed on screen after the tap (a drawer's "Fermer" over the closed drawer).
  host: {'(pointerenter)': 'onPointerEnter($event)', '(pointerleave)': 'onMouseLeave()'},
})
export class TooltipDirective implements OnChanges, OnDestroy {
  protected viewContainerRef = inject(ViewContainerRef);
  protected elementRef = inject(ElementRef);

  @Input() tooltip?: string;
  @Input() tooltipTriggerFor?: TooltipComponent;
  @Input() tooltipPosition: 'left'|'right'|'top'|'bottom' = TooltipPosition.BOTTOM;
  @Input() tooltipDisabled = false;
  @Input() tooltipOffset = 0;
  @Input() tooltipForced = false;
  /**
   * When true, the tooltip shows only if the host element is actually truncated
   * (offsetWidth < scrollWidth), measured at hover. Prefer this over binding
   * `[tooltipDisabled]="!isEllipsisActive(el)"`: a DOM-measuring method in a binding is
   * evaluated only during change detection, so it never re-checks under OnPush (the tooltip
   * gets stuck disabled). Measuring at hover works regardless of CD strategy.
   */
  @Input() tooltipShowOnlyWhenTruncated = false;

  childNode: Node|null = null;
  componentRef: ComponentRef<TooltipComponent>|null = null;
  subscription: Subscription = new Subscription();

  readonly MIN_MARGIN: number = 5;
  readonly POINTER_SIZE: number = 5;

  ngOnChanges() {
    if (this.tooltipForced) {
      this.onMouseEnter();
    }
  }

  onPointerEnter(event: PointerEvent): void {
    if (event.pointerType !== 'touch') {
      this.onMouseEnter();
    }
  }

  onMouseEnter(): void {
    this.destroy();
    if (this.tooltipShowOnlyWhenTruncated && !this.isElementTruncated()) {
      return;
    }
    if (this.tooltipTriggerFor) {
      this.tooltipTriggerFor.maxWidth = 2000;
      this.setTooltipComponentProperties(this.tooltipTriggerFor);
    } else if (this.tooltip && this.tooltip.length > 0) {
      this.componentRef = this.viewContainerRef.createComponent(TooltipComponent);
      this.componentRef.instance.maxWidth = 200;
      this.componentRef.instance.tooltip = this.tooltip.replace('<script>', '').replace('</script>', '');
      // Render the freshly-created view now so its @ViewChild('tooltipContainer') resolves (see TooltipComponent.refresh).
      this.componentRef.instance.refresh();
      this.setTooltipComponentProperties(this.componentRef.instance);
    }
  }

  private isElementTruncated(): boolean {
    const el = this.elementRef.nativeElement as HTMLElement;
    return el.offsetWidth < el.scrollWidth;
  }

  private setTooltipComponentProperties(tooltipComp: TooltipComponent): void {
    if (this.tooltipDisabled) {
      tooltipComp.visible = false;
    } else {
      this.subscription = interval(10)
        .pipe(filter(() => !!tooltipComp.tooltipContainer()), first())
        .subscribe(() => {
          const node = tooltipComp.tooltipContainer()!.nativeElement as HTMLElement;
          this.childNode = document.body.appendChild(node);
          // CDK ≥21 renders dialogs in the browser top layer (Popover API), which paints above
          // any z-index; join the top layer so the tooltip stays visible over open dialogs.
          if (node.showPopover) {
            node.popover = 'manual';
            node.showPopover();
          }
          const trigger = this.elementRef.nativeElement.getBoundingClientRect();
          const tooltip = tooltipComp.tooltipContainer()!.nativeElement.getBoundingClientRect();
          if (this.tooltipPosition === TooltipPosition.LEFT) {
            if (trigger.left - tooltip.width - this.MIN_MARGIN - this.POINTER_SIZE < 0) {
              this.computePositionRight(tooltipComp, trigger, tooltip);
            } else {
              this.computePositionLeft(tooltipComp, trigger, tooltip);
            }
          } else if (this.tooltipPosition === TooltipPosition.RIGHT) {
            if (trigger.right + tooltip.width + this.MIN_MARGIN - this.POINTER_SIZE > window.innerWidth) {
              this.computePositionLeft(tooltipComp, trigger, tooltip);
            } else {
              this.computePositionRight(tooltipComp, trigger, tooltip);
            }
          } else if (this.tooltipPosition === TooltipPosition.TOP) {
            if (trigger.top - tooltip.height - this.MIN_MARGIN - this.POINTER_SIZE < 0) {
              this.computePositionBottom(tooltipComp, trigger, tooltip);
            } else {
              this.computePositionTop(tooltipComp, trigger, tooltip);
            }
          } else if (this.tooltipPosition === TooltipPosition.BOTTOM) {
            if (trigger.bottom + tooltip.height + this.MIN_MARGIN - this.POINTER_SIZE > window.innerHeight) {
              this.computePositionTop(tooltipComp, trigger, tooltip);
            } else {
              this.computePositionBottom(tooltipComp, trigger, tooltip);
            }
          }
          tooltipComp.refresh();
          // Two-frame handshake: frame 1 paints the directional starting offset (and enables the
          // transition), frame 2 flips to visible so the slide animates along the correct axis.
          // A mouseleave in between detaches the node (destroy), so a late frame must not re-show it.
          requestAnimationFrame(() => {
            if (!this.childNode) return;
            tooltipComp.animate = true;
            tooltipComp.refresh();
            requestAnimationFrame(() => {
              if (!this.childNode) return;
              tooltipComp.visible = true;
              tooltipComp.refresh();
            });
          });
        });
    }
  }

  private computePositionLeft(tooltipComp: TooltipComponent, trigger: DOMRect, tooltip: DOMRect): void {
    tooltipComp.position = TooltipPosition.LEFT;
    tooltipComp.left = trigger.left - tooltip.width - this.POINTER_SIZE - this.tooltipOffset;
    this.computeVerticalPosition(tooltipComp, trigger, tooltip);
  }

  private computePositionRight(tooltipComp: TooltipComponent, trigger: DOMRect, tooltip: DOMRect): void {
    tooltipComp.position = TooltipPosition.RIGHT;
    tooltipComp.left = trigger.right + this.POINTER_SIZE + this.tooltipOffset;
    this.computeVerticalPosition(tooltipComp, trigger, tooltip);
  }

  private computeVerticalPosition(tooltipComp: TooltipComponent, trigger: DOMRect, tooltip: DOMRect): void {
    const top: number = (trigger.top + (trigger.height / 2)) - (tooltip.height / 2);
    tooltipComp.top = Math.min(Math.max(this.MIN_MARGIN, top), window.innerHeight - tooltip.height - this.MIN_MARGIN);
    tooltipComp.delta = Math.min(Math.max(0, this.MIN_MARGIN - top), tooltipComp.top - top);
  }

  private computePositionBottom(tooltipComp: TooltipComponent, trigger: DOMRect, tooltip: DOMRect): void {
    tooltipComp.position = TooltipPosition.BOTTOM;
    tooltipComp.top = trigger.bottom + this.POINTER_SIZE + this.tooltipOffset;
    this.computeHorizontalPosition(tooltipComp, trigger, tooltip);
  }

  private computePositionTop(tooltipComp: TooltipComponent, trigger: DOMRect, tooltip: DOMRect): void {
    tooltipComp.position = TooltipPosition.TOP;
    tooltipComp.top = trigger.top - tooltip.height - this.POINTER_SIZE - this.tooltipOffset;
    this.computeHorizontalPosition(tooltipComp, trigger, tooltip);
  }

  private computeHorizontalPosition(tooltipComp: TooltipComponent, trigger: DOMRect, tooltip: DOMRect): void {
    const left: number = (trigger.left + (trigger.width / 2)) - (tooltip.width / 2);
    tooltipComp.left = Math.min(Math.max(this.MIN_MARGIN, left), window.innerWidth - tooltip.width - this.MIN_MARGIN);
    tooltipComp.delta = Math.min(Math.max(0, this.MIN_MARGIN - left), tooltipComp.left - left);
  }

  onMouseLeave(): void {
    this.destroy();
  }

  /** @param refresh re-render the declared tooltip so it hides at once; false while the host view is being torn down. */
  destroy(refresh = true): void {
    this.subscription.unsubscribe();
    if (this.tooltipTriggerFor) {
      const wasShown = this.tooltipTriggerFor.visible || this.tooltipTriggerFor.animate;
      this.tooltipTriggerFor.visible = false;
      this.tooltipTriggerFor.animate = false;
      if (refresh && wasShown) {
        this.tooltipTriggerFor.refresh();
      }
    }
    if (this.componentRef) {
      this.componentRef.instance.visible = false;
      this.componentRef.destroy();
      this.componentRef = null;
    }
    if (this.childNode) {
      document.body.removeChild(this.childNode);
      this.childNode = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy(false);
  }
}

enum TooltipPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right'
}
